import { xxhash64 } from './xxhash';
import { KOKORO_VOICES } from './voiceMapping';
import { voiceBundle } from './voiceBundle';
import type { ArcProperties } from './types';

export const SALT_ARC_CURVE = 0x0010n;
export const SALT_ARC_HARMONIC = 0x0011n;
export const SALT_ARC_SPECTRAL = 0x0012n;

const LO32 = (1n << 32n) - 1n;

/**
 * Symmetry-aware pair hash. Sorts (a, b) before hashing so
 * pairHash(a, b, salt) === pairHash(b, a, salt).
 */
function pairHash(
  idxA: number,
  idxB: number,
  salt: bigint,
  worldSeed: bigint,
): bigint {
  const [lo, hi] = idxA <= idxB ? [idxA, idxB] : [idxB, idxA];
  const buf = new Uint8Array(16);
  const view = new DataView(buf.buffer);
  view.setBigUint64(0, BigInt(lo), true);
  view.setBigUint64(8, BigInt(hi), true);
  return xxhash64(buf, worldSeed ^ salt);
}

function toUnit(h: bigint): number {
  return Number(h & LO32) / 4294967296;
}

function cosineSim(a: Float32Array, b: Float32Array): number {
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  const denom = Math.sqrt(na * nb);
  if (denom < 1e-9) return 0;
  // Normalise [-1, 1] to [0, 1].
  return Math.max(0, Math.min(1, (dot / denom + 1) * 0.5));
}

/**
 * Build the 26×26 arc-property table for the given world seed. Requires
 * voiceBundle.ensureReady() to have resolved. Diagonal cells are the
 * identity arc (curveWarp=0.5, harmonicWeight=0, spectralSkew=0, kinship=1).
 */
export function buildArcTable(worldSeed: bigint): ArcProperties[][] {
  const rows = voiceBundle.getRows();
  if (!rows) throw new Error('voice bundle not loaded');
  const N = KOKORO_VOICES.length;
  const table: ArcProperties[][] = new Array(N);
  for (let a = 0; a < N; a++) {
    table[a] = new Array(N);
    for (let b = 0; b < N; b++) {
      if (a === b) {
        table[a][b] = {
          curveWarp: 0.5,
          harmonicWeight: 0.0,
          spectralSkew: 0.0,
          kinship: 1.0,
        };
        continue;
      }
      const hC = pairHash(a, b, SALT_ARC_CURVE, worldSeed);
      const hH = pairHash(a, b, SALT_ARC_HARMONIC, worldSeed);
      const hS = pairHash(a, b, SALT_ARC_SPECTRAL, worldSeed);
      table[a][b] = {
        curveWarp: 0.2 + toUnit(hC) * 0.6,
        harmonicWeight: toUnit(hH) * 0.3,
        spectralSkew: toUnit(hS) * 0.30 - 0.15,
        kinship: cosineSim(rows[a][0], rows[b][0]),
      };
    }
  }
  return table;
}
